// src/modules/Admin/pages/Users/index.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import { useAuth } from '../../../../contexts/AuthContext';
import {
  FaUsers,
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaCheck,
  FaUserShield,
  FaUserTie,
  FaEye,
  FaSearch,
  FaSpinner,
  FaExclamationTriangle
} from 'react-icons/fa';

// Configuração de roles
const ROLES_CONFIG = {
  admin: { label: 'Administrador', color: '#8b5cf6', icon: FaUserShield },
  seller: { label: 'Vendedor', color: '#3b82f6', icon: FaUserTie },
  viewer: { label: 'Visualizador', color: '#6b7280', icon: FaEye }
};

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #666;
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #111;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #333;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  min-width: 150px;
  flex: 1;

  .value {
    font-size: 28px;
    font-weight: 700;
    color: #111;
    margin-bottom: 4px;
  }

  .label {
    font-size: 13px;
    color: #666;
  }
`;

const FiltersRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;

  input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;
    transition: all 200ms;

    &:focus {
      outline: none;
      border-color: #333;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }
  }

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 16px;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Td = styled.td`
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #f0f0f0;
`;

const UserDetails = styled.div`
  .name {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 2px;
  }

  .email {
    font-size: 12px;
    color: #666;
  }
`;

const RoleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 20px;
  background: ${props => ROLES_CONFIG[props.$role]?.color || '#666'}15;
  color: ${props => ROLES_CONFIG[props.$role]?.color || '#666'};

  svg {
    font-size: 11px;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 12px;
  background: ${props => props.$active ? '#ecfdf5' : '#fef2f2'};
  color: ${props => props.$active ? '#059669' : '#dc2626'};
`;

const DateText = styled.span`
  font-size: 13px;
  color: #666;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: ${props => props.$danger ? '#fef2f2' : '#f5f5f5'};
    color: ${props => props.$danger ? '#dc2626' : '#111'};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;

  svg {
    font-size: 48px;
    color: #ddd;
    margin-bottom: 16px;
  }

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  p {
    font-size: 14px;
    color: #666;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  color: #666;

  svg {
    animation: spin 1s linear infinite;
    margin-right: 10px;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 16px;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111;
`;

const ModalClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 8px;
  background: #f5f5f5;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #e5e5e5;
    color: #111;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }

  &:disabled {
    background: #f5f5f5;
    color: #999;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 200ms;

  &:focus {
    outline: none;
    border-color: #111;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #333;

  input {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
`;

const Button = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 200ms;

  ${props => props.$primary ? `
    background: #111;
    color: white;
    border: none;
    &:hover:not(:disabled) { background: #333; }
  ` : `
    background: white;
    color: #666;
    border: 1px solid #e5e5e5;
    &:hover { background: #f5f5f5; }
  `}

  ${props => props.$danger && `
    background: #ef4444;
    color: white;
    border: none;
    &:hover:not(:disabled) { background: #dc2626; }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const WarningBox = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #fffbeb;
  border-radius: 8px;
  border: 1px solid #fde68a;
  margin-bottom: 20px;

  svg {
    color: #f59e0b;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .content {
    .title {
      font-size: 14px;
      font-weight: 600;
      color: #92400e;
      margin-bottom: 4px;
    }

    .message {
      font-size: 13px;
      color: #a16207;
    }
  }
`;

const Users = () => {
  const { user, isAdmin, logActivity } = useAuth();
  const [users, setUsers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'seller',
    is_active: true,
    job_id: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchJobs();
  }, []);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*, jobs(id, title)')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Erro ao buscar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const { data, error } = await supabase
        .from('jobs')
        .select('id, title, status')
        .order('title');

      if (error) throw error;
      setJobs(data || []);
    } catch (error) {
      console.error('Erro ao buscar vagas:', error);
    }
  };

  const filteredUsers = users.filter(u => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch =
      u.name?.toLowerCase().includes(searchLower) ||
      u.email?.toLowerCase().includes(searchLower);
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const stats = {
    total: users.length,
    admins: users.filter(u => u.role === 'admin').length,
    sellers: users.filter(u => u.role === 'seller').length,
    active: users.filter(u => u.is_active).length
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'seller',
      is_active: true,
      job_id: ''
    });
    setShowModal(true);
  };

  const openEditModal = (userToEdit) => {
    setEditingUser(userToEdit);
    setFormData({
      name: userToEdit.name || '',
      email: userToEdit.email,
      role: userToEdit.role || 'viewer',
      is_active: userToEdit.is_active !== false,
      job_id: userToEdit.job_id || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'seller',
      is_active: true,
      job_id: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) return;

    setSaving(true);
    try {
      if (editingUser) {
        // Atualizar usuário existente
        const { error } = await supabase
          .from('admin_users')
          .update({
            name: formData.name.trim(),
            role: formData.role,
            is_active: formData.is_active,
            job_id: formData.job_id || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingUser.id);

        if (error) throw error;

        // Registrar atividade
        await logActivity('update_user', 'user', editingUser.id, formData.name || formData.email, {
          role: formData.role,
          is_active: formData.is_active,
          job_id: formData.job_id
        });

        // Atualizar lista local com dados da vaga
        const selectedJob = jobs.find(j => j.id === formData.job_id);
        setUsers(users.map(u =>
          u.id === editingUser.id
            ? { ...u, name: formData.name.trim(), role: formData.role, is_active: formData.is_active, job_id: formData.job_id, jobs: selectedJob }
            : u
        ));
      } else {
        // Criar novo usuário
        const { data, error } = await supabase
          .from('admin_users')
          .insert({
            name: formData.name.trim(),
            email: formData.email.toLowerCase().trim(),
            role: formData.role,
            is_active: formData.is_active,
            job_id: formData.job_id || null,
            invited_by: user?.email
          })
          .select('*, jobs(id, title)')
          .single();

        if (error) throw error;

        // Registrar atividade
        await logActivity('create_user', 'user', data.id, formData.name || formData.email, {
          role: formData.role,
          job_id: formData.job_id
        });

        setUsers([data, ...users]);
      }

      closeModal();
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      alert('Erro ao salvar usuário. Verifique se o email já está cadastrado.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', showDeleteConfirm.id);

      if (error) throw error;

      // Registrar atividade
      await logActivity('delete_user', 'user', showDeleteConfirm.id, showDeleteConfirm.name || showDeleteConfirm.email);

      setUsers(users.filter(u => u.id !== showDeleteConfirm.id));
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert('Erro ao excluir usuário.');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Verificar se é admin
  if (!isAdmin) {
    return (
      <Container>
        <EmptyState>
          <FaUserShield />
          <h3>Acesso Restrito</h3>
          <p>Apenas administradores podem acessar esta página.</p>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>
          <FaUsers />
          Gerenciar Usuários
        </Title>
        <AddButton onClick={openCreateModal}>
          <FaPlus />
          Novo Usuário
        </AddButton>
      </Header>

      <StatsRow>
        <StatCard>
          <div className="value">{stats.total}</div>
          <div className="label">Total de Usuários</div>
        </StatCard>
        <StatCard>
          <div className="value">{stats.admins}</div>
          <div className="label">Administradores</div>
        </StatCard>
        <StatCard>
          <div className="value">{stats.sellers}</div>
          <div className="label">Vendedores</div>
        </StatCard>
        <StatCard>
          <div className="value">{stats.active}</div>
          <div className="label">Usuários Ativos</div>
        </StatCard>
      </StatsRow>

      <FiltersRow>
        <SearchInput>
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        <FilterSelect
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">Todas as funções</option>
          <option value="admin">Administradores</option>
          <option value="seller">Vendedores</option>
          <option value="viewer">Visualizadores</option>
        </FilterSelect>
      </FiltersRow>

      <Card>
        {loading ? (
          <LoadingState>
            <FaSpinner />
            Carregando usuários...
          </LoadingState>
        ) : filteredUsers.length === 0 ? (
          <EmptyState>
            <FaUsers />
            <h3>Nenhum usuário encontrado</h3>
            <p>Adicione usuários para que possam acessar o painel.</p>
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Usuário</Th>
                <Th>Função</Th>
                <Th>Vaga</Th>
                <Th>Status</Th>
                <Th>Último Acesso</Th>
                <Th style={{ width: '100px' }}>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((u) => {
                const RoleIcon = ROLES_CONFIG[u.role]?.icon || FaEye;
                return (
                  <tr key={u.id}>
                    <Td>
                      <UserInfo>
                        <UserAvatar
                          src={u.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(u.name || u.email)}&background=333&color=fff`}
                          alt={u.name || u.email}
                        />
                        <UserDetails>
                          <div className="name">{u.name || 'Sem nome'}</div>
                          <div className="email">{u.email}</div>
                        </UserDetails>
                      </UserInfo>
                    </Td>
                    <Td>
                      <RoleBadge $role={u.role}>
                        <RoleIcon />
                        {ROLES_CONFIG[u.role]?.label || u.role}
                      </RoleBadge>
                    </Td>
                    <Td>
                      {u.jobs?.title || <span style={{ color: '#999' }}>Não vinculado</span>}
                    </Td>
                    <Td>
                      <StatusBadge $active={u.is_active !== false}>
                        {u.is_active !== false ? 'Ativo' : 'Inativo'}
                      </StatusBadge>
                    </Td>
                    <Td>
                      <DateText>{formatDate(u.last_login)}</DateText>
                    </Td>
                    <Td>
                      <ActionsCell>
                        <ActionButton onClick={() => openEditModal(u)} title="Editar">
                          <FaEdit />
                        </ActionButton>
                        {u.email !== user?.email && (
                          <ActionButton
                            $danger
                            onClick={() => setShowDeleteConfirm(u)}
                            title="Excluir"
                          >
                            <FaTrash />
                          </ActionButton>
                        )}
                      </ActionsCell>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card>

      {/* Modal de Criar/Editar */}
      {showModal && (
        <ModalOverlay onClick={closeModal}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
              </ModalTitle>
              <ModalClose onClick={closeModal}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label>Nome</Label>
                  <Input
                    type="text"
                    placeholder="Nome completo"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    placeholder="email@empresa.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    disabled={editingUser}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Função *</Label>
                  <Select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  >
                    <option value="admin">Administrador - Acesso total</option>
                    <option value="seller">Vendedor - Gerencia leads atribuídos</option>
                    <option value="viewer">Visualizador - Apenas visualização</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label>Vaga Vinculada</Label>
                  <Select
                    value={formData.job_id}
                    onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
                  >
                    <option value="">Nenhuma vaga vinculada</option>
                    {jobs.map(job => (
                      <option key={job.id} value={job.id}>
                        {job.title} {job.status !== 'active' ? `(${job.status})` : ''}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <CheckboxLabel>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                    Usuário ativo
                  </CheckboxLabel>
                </FormGroup>

                {!editingUser && (
                  <WarningBox>
                    <FaExclamationTriangle />
                    <div className="content">
                      <div className="title">Importante</div>
                      <div className="message">
                        O usuário precisará fazer login com uma conta Google que tenha este email cadastrado.
                      </div>
                    </div>
                  </WarningBox>
                )}

                <ModalActions>
                  <Button type="button" onClick={closeModal}>
                    Cancelar
                  </Button>
                  <Button type="submit" $primary disabled={saving || !formData.email.trim()}>
                    {saving ? <FaSpinner /> : <FaCheck />}
                    {editingUser ? 'Salvar Alterações' : 'Criar Usuário'}
                  </Button>
                </ModalActions>
              </form>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}

      {/* Modal de Confirmação de Exclusão */}
      {showDeleteConfirm && (
        <ModalOverlay onClick={() => setShowDeleteConfirm(null)}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Confirmar Exclusão</ModalTitle>
              <ModalClose onClick={() => setShowDeleteConfirm(null)}>
                <FaTimes />
              </ModalClose>
            </ModalHeader>
            <ModalBody>
              <WarningBox>
                <FaExclamationTriangle />
                <div className="content">
                  <div className="title">Atenção!</div>
                  <div className="message">
                    Você está prestes a excluir o usuário <strong>{showDeleteConfirm.name || showDeleteConfirm.email}</strong>.
                    Esta ação não pode ser desfeita.
                  </div>
                </div>
              </WarningBox>

              <ModalActions>
                <Button type="button" onClick={() => setShowDeleteConfirm(null)}>
                  Cancelar
                </Button>
                <Button type="button" $danger onClick={handleDelete} disabled={saving}>
                  {saving ? <FaSpinner /> : <FaTrash />}
                  Excluir Usuário
                </Button>
              </ModalActions>
            </ModalBody>
          </Modal>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default Users;
